import { Link } from 'react-router-dom';
import { formatCalendarDate } from '../../lib/format';
import { EmptyState } from '../../components/ui/EmptyState';
import {
  daysLate,
  longestSilence,
  sortForDisplay,
  tally,
  type OccurrenceRow,
} from './occurrence-model';

/**
 * ONE TEMPLATE'S FULL HISTORY — every expected period it has generated, in all three states.
 *
 * ⭐ The GLOBAL missed view is `MissedList`, backed by `GET /recurrences/missed`. This is the
 * drill-down: "what has this particular duty actually done?", which needs the completed and upcoming
 * periods too, because a run of misses only means something against the completions around it.
 *
 * ⛔ A missed row still renders exactly as it does in the global view — a red rule, a MISSED chip and
 * the days late, sorted first. A subtle grey row among fifty completed ones is how a 14-day gap and
 * three lost sessions got through before this existed, and being one level deeper does not make it
 * less true here.
 *
 * ⭐ The longest UNBROKEN run is called out separately from the count: three scattered misses in a
 * quarter is a different fact from three consecutive weeks of silence, and only the second means the
 * duty has stopped.
 */
export function OccurrenceList({ rows, today }: { rows: readonly OccurrenceRow[]; today: string }) {
  const totals = tally(rows);
  const silence = longestSilence(rows);
  const sorted = sortForDisplay(rows);

  if (rows.length === 0) {
    return (
      <EmptyState
        title="This template has generated no periods yet"
        detail="The request succeeded and returned none. ⚠️ That is not a clean record — it means the generator has not run for this template, so there is nothing yet that COULD have been missed."
      />
    );
  }

  return (
    <div>
      {totals.missed > 0 ? (
        <div
          role="alert"
          className="mx-3 mb-3 rounded-md border-2 border-red-500 bg-red-50 px-3 py-3 text-red-900 sm:mx-4 dark:border-red-600 dark:bg-red-950/60 dark:text-red-100"
        >
          <p className="text-base font-bold">
            <span aria-hidden="true">⛔ </span>
            {totals.missed} period{totals.missed === 1 ? '' : 's'} missed
          </p>
          <p className="mt-0.5 text-sm">
            {silence > 1 ? (
              <>
                The longest unbroken silence is <strong>{silence} periods in a row</strong>. A duty
                cannot detect its own silence — that is why every expected period is a row.
              </>
            ) : (
              <>
                Each of these is a period that was expected and for which no ticket was ever created.
              </>
            )}
          </p>
        </div>
      ) : (
        <p className="mx-3 mb-3 rounded-md border border-[color:var(--color-line)] px-3 py-2 text-sm sm:mx-4">
          <span aria-hidden="true">✅ </span>
          Every expected period has a ticket — {totals.done} completed, {totals.upcoming} still to
          come.
          {/* ⭐ Said explicitly. "No red rows" and "we did not look" are the same picture otherwise. */}
        </p>
      )}

      <ul className="space-y-2 px-3 pb-4 sm:px-4">
        {sorted.map((row) => (
          <OccurrenceRowItem key={row.occurrence.id} row={row} today={today} />
        ))}
      </ul>
    </div>
  );
}

function OccurrenceRowItem({ row, today }: { row: OccurrenceRow; today: string }) {
  const { occurrence, recurrence, state } = row;
  const title = recurrence?.title ?? `Template #${occurrence.recurrence_id}`;

  if (state === 'missed') {
    return (
      <li
        data-state="missed"
        aria-label={`${title} — missed, expected ${occurrence.expected_on}`}
        className="rounded border-l-8 border-red-500 bg-red-50 px-3 py-3 dark:border-red-600 dark:bg-red-950/50"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase">
            Missed
          </span>
          <span className="text-sm font-bold text-red-900 dark:text-red-100">{title}</span>
        </div>
        <p className="mt-1 text-sm text-red-900 dark:text-red-100">
          Expected <strong>{formatCalendarDate(occurrence.expected_on)}</strong> —{' '}
          <strong>{daysLate(occurrence.expected_on, today)} days late</strong>. No ticket was ever
          created for this period.
        </p>
        {recurrence === null ? (
          <p className="mt-1 text-xs text-red-900/80 dark:text-red-100/80">
            ⚠️ Its template is not in the loaded list — shown anyway, by <code>recurrence_id</code>.
          </p>
        ) : null}
      </li>
    );
  }

  if (state === 'done') {
    return (
      <li
        data-state="done"
        aria-label={`${title} — done, expected ${occurrence.expected_on}`}
        className="rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] px-3 py-2.5"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="rounded bg-[color:var(--color-line)] px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
            Done
          </span>
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          {formatCalendarDate(occurrence.expected_on)} —{' '}
          {/* ⚠️ The key is optional on the wire; without it we link by id and say so rather than
              assembling a key from a project this row does not know. */}
          <Link
            to={`/backlog?ticket=${occurrence.ticket_id}`}
            className="font-semibold text-[color:var(--color-accent)] underline"
          >
            {occurrence.ticket_key ?? `ticket #${occurrence.ticket_id}`}
          </Link>
        </p>
      </li>
    );
  }

  return (
    <li
      data-state="upcoming"
      aria-label={`${title} — upcoming, expected ${occurrence.expected_on}`}
      className="rounded border border-dashed border-[color:var(--color-line)] px-3 py-2.5"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="rounded border border-[color:var(--color-line)] px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
          Upcoming
        </span>
        <span className="text-sm">{title}</span>
      </div>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
        Expected {formatCalendarDate(occurrence.expected_on)} — not due yet.
      </p>
    </li>
  );
}
